
import React, { useState, useEffect } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import { userAPI } from '../../api/user.api';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import styles from './Dashboard.module.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false); // Start closed by default
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if we're on mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1024;
      setIsMobile(mobile);
      
      // Auto-close sidebar on mobile, auto-open on desktop
      if (mobile) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };
    
    // Initial check
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Toggle sidebar function
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Close sidebar on mobile when clicking outside
  const closeSidebar = () => {
    if (window.innerWidth <= 1024) {
      setSidebarOpen(false);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        navigate('/login', { replace: true });
        return;
      }

      try {
        const userData = await userAPI.getCurrentUser();
        
        if (userData && userData.message && userData.message.includes('error')) {
          throw new Error(userData.message);
        }
        
        if (!userData || !userData.code) {
          throw new Error('Invalid user data received');
        }
        
        setUser({
          fullName: userData.fullName || 'User',
          email: userData.email || '',
          code: userData.code || '',
          roleId: userData.roleId || 3,
          roleDescription: userData.roleDescription || 'User',
          customerCode: userData.customerCode || ''
        });
        setError(null);
        
      } catch (err) {
        setError(err.message);
        
        if (err.message.includes('401') || 
            err.message.includes('Unauthorized') || 
            err.message.includes('Invalid token') ||
            err.message.includes('No authentication') ||
            err.message.includes('Token expired')) {
          
          localStorage.removeItem('auth_token');
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 100);
        }
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  // Loading state
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  // Error state but not auth error
  if (error && !user) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorIcon}>⚠️</div>
        <h2>Authentication Error</h2>
        <p>{error}</p>
        <button 
          onClick={() => {
            localStorage.removeItem('auth_token');
            navigate('/login', { replace: true });
          }}
          className={styles.loginButton}
        >
          Go to Login
        </button>
      </div>
    );
  }

  // If no user after loading
  if (!user) {
    return (
      <div className={styles.authContainer}>
        <div className={styles.authIcon}>🔒</div>
        <h2>Authentication Required</h2>
        <p>Please log in to access the dashboard.</p>
        <button 
          onClick={() => navigate('/login')}
          className={styles.loginButton}
        >
          Go to Login
        </button>
      </div>
    );
  }

  console.log('✅ Dashboard rendered with user:', user);

  return (
    <>
      <Navbar 
        onMenuToggle={toggleSidebar} 
        user={user} 
        isSidebarOpen={sidebarOpen}
      />
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={closeSidebar} 
        user={user} 
      />
      <main className={`${styles.mainContainer} ${!sidebarOpen ? styles.sidebarCollapsed : ''}`}>
        <div className={`${styles.contentWrapper} ${sidebarOpen ? styles.sidebarOpen : styles.sidebarClosed}`}>
          {/* PASS sidebarOpen IN THE CONTEXT */}
          <Outlet context={{ user, sidebarOpen }} />
        </div>
      </main>
    </>
  );
};

export default Dashboard;